import supabase from '../supabaseClient'
import { TABLE_NAMES } from '../config'

export async function ensureInitialRatings(userId) {
  const inserts = [
    supabase.from('Ratings').insert([{ user_id: userId, rating: 500 }]),
    supabase
      .from('Ratings_padel')
      .insert([{ user_id: userId, rating: 500 }]),
    supabase
      .from('Ratings_basketball')
      .insert([{ user_id: userId, rating: 500 }]),
  ]
  const results = await Promise.all(inserts)
  const failed = results.find((r) => r.error)
  if (failed?.error) throw failed.error
}

/**
 * Optional signup-funnel columns from signup_flow_migration.sql.
 * Non-fatal when the migration has not been applied yet.
 */
export async function tryWriteSignupExtras(userId, extras) {
  const { error } = await supabase
    .from(TABLE_NAMES.USERS)
    .update(extras)
    .eq('user_id', userId)
  if (error) {
    console.warn(
      'Signup metadata not saved (run src/sql/signup_flow_migration.sql):',
      error.message,
    )
  }
}

/**
 * Creates or updates the signed-in user's Users row when an academy, community,
 * or venue organizer books an activation call (no org entity yet).
 */
export async function persistSignupUserOnActivationCallBooked({
  persona,
  fullName = '',
  email = '',
  phone = '',
  organizationName = '',
  bookedAt = '',
  onboardingData = {},
}) {
  if (persona !== 'academy' && persona !== 'venue' && persona !== 'community') {
    return { ok: false, error: 'Invalid persona for activation-call signup.' }
  }

  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser()

  if (userError || !user) {
    return {
      ok: false,
      error: 'You are not signed in. Please sign in again.',
    }
  }

  const displayName =
    (fullName || '').trim() ||
    user.user_metadata?.full_name ||
    user.user_metadata?.name ||
    ''
  const userEmail = (email || user.email || '').trim()
  const phoneTrimmed = (phone || user.phone || '').trim()
  const orgName = (organizationName || '').trim()

  const { data: existing, error: fetchError } = await supabase
    .from(TABLE_NAMES.USERS)
    .select(
      'user_id, name, email, coach, academy_id, venue_owner, community_id, community_manager',
    )
    .eq('user_id', user.id)
    .maybeSingle()

  if (fetchError) {
    return {
      ok: false,
      error: fetchError.message || 'Could not load your profile.',
    }
  }

  const userPayload = {
    email: userEmail || null,
    name: displayName || null,
  }

  if (phoneTrimmed) {
    userPayload.phone = phoneTrimmed
  }

  if (persona === 'academy' && !existing?.academy_id) {
    userPayload.coach = 'true'
  }

  if (persona === 'community' && !existing?.community_id) {
    userPayload.community_manager = true
  }

  if (persona === 'venue') {
    const currentVenue = String(existing?.venue_owner || '').trim()
    if (!currentVenue) {
      userPayload.venue_owner = orgName || 'pending'
    }
  }

  if (existing) {
    const { error: updateError } = await supabase
      .from(TABLE_NAMES.USERS)
      .update(userPayload)
      .eq('user_id', user.id)

    if (updateError) {
      return {
        ok: false,
        error: updateError.message || 'Could not save your profile.',
      }
    }
  } else {
    const insertRow = {
      user_id: user.id,
      ...userPayload,
      privacy_policy_terms: 'yes',
    }
    if (persona === 'academy') {
      insertRow.coach = 'true'
    }
    if (persona === 'community') {
      insertRow.community_manager = true
    }
    if (persona === 'venue') {
      insertRow.venue_owner = orgName || 'pending'
    }

    const { error: insertError } = await supabase
      .from(TABLE_NAMES.USERS)
      .insert([insertRow])

    if (insertError) {
      return {
        ok: false,
        error: insertError.message || 'Could not create your profile.',
      }
    }

    try {
      await ensureInitialRatings(user.id)
    } catch (ratingErr) {
      console.error('Initial ratings insert failed', ratingErr)
    }
  }

  const onboardingPayload = {
    ...onboardingData,
    ...(bookedAt ? { activation_call_booked_at: bookedAt } : {}),
  }

  await tryWriteSignupExtras(user.id, {
    persona,
    account_status:
      persona === 'community' ? 'activated' : 'setup_mode',
    organization_name: orgName || null,
    signup_step: 2,
    activation_call_booked: true,
    onboarding_data:
      Object.keys(onboardingPayload).length > 0 ? onboardingPayload : null,
  })

  if (persona === 'venue') {
    supabase.functions
      .invoke('facility-emails', { body: { kind: 'welcome' } })
      .then(({ error: emailError }) => {
        if (emailError) console.warn('Facility welcome email failed', emailError)
      })
      .catch((emailErr) => {
        console.warn('Facility welcome email failed', emailErr)
      })
  }

  return { ok: true }
}
